/**
 * Shared helpers for the buyer order-history pages (F1).
 */

import { FULFILLMENT_STATES } from "@/queries/orders/order-insights";

/**
 * Display label for an order. Prefer the custom display id (e.g. "SS-26543646")
 * generated by the backend ORDER module; fall back to the autoincrement
 * `display_id` (e.g. "#1") when it isn't set.
 */
export function formatOrderNumber(order: {
  custom_display_id?: string | null;
  display_id?: number | null;
}): string {
  return order.custom_display_id ?? `#${order.display_id}`;
}

export interface OrderStatusBadge {
  label: string;
  className: string;
}

/**
 * Maps the core Medusa `order.status` to a buyer-facing badge.
 *
 * TODO(salonstocker/api#100): once the store order read path exposes the
 * computed `fulfillment_status`, switch to richer buyer-facing states
 * (Processing / In Transit / Delivered) instead of the core order status.
 */
export function getOrderStatusBadge(status: string): OrderStatusBadge {
  switch (status) {
    case "completed":
      return {
        label: "Completed",
        className:
          "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
      };
    case "canceled":
      return {
        label: "Cancelled",
        className:
          "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
      };
    case "requires_action":
      return {
        label: "Action needed",
        className:
          "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
      };
    case "pending":
      return {
        label: "Processing",
        className:
          "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
      };
    default:
      return {
        label: status.charAt(0).toUpperCase() + status.slice(1),
        className:
          "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
      };
  }
}

const BADGE = {
  green: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  blue: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  indigo:
    "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300",
  amber: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  red: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  gray: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
} as const;

/**
 * Maps the computed `fulfillment_status` (from `/store/order-history`) to the
 * primary buyer-facing "where is my order" badge.
 */
export function getFulfillmentStatusBadge(
  status?: string | null
): OrderStatusBadge {
  switch (status) {
    case "delivered":
      return { label: "Delivered", className: BADGE.green };
    case "partially_delivered":
      return { label: "Partially delivered", className: BADGE.green };
    case "shipped":
      return { label: "In transit", className: BADGE.blue };
    case "partially_shipped":
      return { label: "Partially shipped", className: BADGE.blue };
    case "fulfilled":
    case "partially_fulfilled":
      return { label: "Preparing", className: BADGE.indigo };
    case "canceled":
      return { label: "Cancelled", className: BADGE.red };
    case "not_fulfilled":
    default:
      return { label: "Processing", className: BADGE.amber };
  }
}

/**
 * Coarse fulfillment buckets for the `/account/orders` status tabs.
 *
 * Each bucket maps a buyer-facing tab to the set of canonical
 * `fulfillment_status` values the `/store/order-history` endpoint filters on
 * (its `fulfillment_status` param accepts one or more values - see
 * salonstocker/api#130). The `states` come from the shared `FULFILLMENT_STATES`
 * (the order-insights single source the hero-stat predicates also read), so the
 * tabs and the stats read the same state groups. The one deliberate exception is
 * a cancelled order with real supplier progress (the api checks delivered,
 * shipped and packed before cancelled): its `fulfillment_status` keeps that
 * progress, so the Delivered, In transit or Preparing tab and badge show it while
 * the stats count it as cancelled (a Shopify cancel reads Cancelled instead; see
 * CONTEXT.md). Kept next to
 * {@link getFulfillmentStatusBadge} so the tabs and the row badges stay
 * consistent: every state a bucket owns renders a badge in that bucket's colour
 * family (Processing amber, Preparing indigo, In transit blue, Delivered green,
 * Cancelled red).
 */
export const FULFILLMENT_BUCKET_IDS = [
  "processing",
  "preparing",
  "in_transit",
  "delivered",
  "cancelled"
] as const;

export type FulfillmentBucketId = (typeof FULFILLMENT_BUCKET_IDS)[number];

export interface FulfillmentBucket {
  /** URL search value + tab id. */
  id: FulfillmentBucketId;
  /** Tab label. */
  label: string;
  /** Canonical `fulfillment_status` values this bucket selects. */
  states: string[];
}

export const FULFILLMENT_BUCKETS: readonly FulfillmentBucket[] = [
  {
    id: "processing",
    label: "Processing",
    states: [...FULFILLMENT_STATES.processing]
  },
  {
    id: "preparing",
    label: "Preparing",
    states: [...FULFILLMENT_STATES.preparing]
  },
  {
    id: "in_transit",
    label: "In transit",
    states: [...FULFILLMENT_STATES.in_transit]
  },
  {
    id: "delivered",
    label: "Delivered",
    states: [...FULFILLMENT_STATES.delivered]
  },
  {
    id: "cancelled",
    label: "Cancelled",
    states: [...FULFILLMENT_STATES.cancelled]
  }
];

/** Look up a bucket by its tab/URL id. */
export function getFulfillmentBucket(
  id?: string | null
): FulfillmentBucket | undefined {
  return FULFILLMENT_BUCKETS.find((bucket) => bucket.id === id);
}

/** `payment_status` values that mean the buyer still has to act or pay. */
const PAYMENT_STILL_TO_PAY = ["not_paid", "awaiting", "requires_action"];

/**
 * Maps the computed `payment_status` to a badge, but only when it warrants the
 * buyer's attention (unpaid / awaiting / refunded). Returns `null` for the
 * normal paid states so rows stay uncluttered - relevant for bank-transfer
 * orders that sit `not_paid` until reconciled.
 *
 * For a cancelled order the "still to pay" states are dropped, as there is
 * nothing left to act on; the refund states stay, as they report money that
 * really moved.
 */
export function getPaymentStatusBadge(
  status?: string | null,
  { cancelled = false }: { cancelled?: boolean } = {}
): OrderStatusBadge | null {
  if (cancelled && PAYMENT_STILL_TO_PAY.includes(status ?? "")) {
    return null;
  }
  switch (status) {
    case "not_paid":
    case "awaiting":
      return { label: "Awaiting payment", className: BADGE.amber };
    case "requires_action":
      return { label: "Payment action needed", className: BADGE.amber };
    case "refunded":
      return { label: "Refunded", className: BADGE.gray };
    case "partially_refunded":
      return { label: "Partially refunded", className: BADGE.gray };
    default:
      // captured / authorized / partially_captured - nothing to flag
      return null;
  }
}

/** Formats an order date for the history rows and detail header. */
export function formatOrderDate(value: string | Date): string {
  return new Intl.DateTimeFormat("en-AU", { dateStyle: "medium" }).format(
    new Date(value)
  );
}
